
import { DeliveryMethod, PaymentFamily, DeliveryStatus, OrderStatus } from '../types';
import i18n from '../i18n';
import { paymentMethodLocaleKey } from './paymentMethodDisplay';

export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const locale = i18n.language === 'ar' ? 'ar-TN' : 'fr-FR';
  return new Date(dateString).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatCurrency = (amount: number | undefined | null): string => {
  const value = amount != null && !Number.isNaN(amount) ? Number(amount) : 0;
  const isArabic = i18n.language.startsWith('ar');

  if (isArabic) {
    return `${value.toFixed(3)} دت`;
  }

  const locale = 'fr-TN';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'TND',
  }).format(value);
};

export const formatNumber = (amount: number | undefined | null): string => {
  const value = amount != null && !Number.isNaN(amount) ? Number(amount) : 0;
  const isArabic = i18n.language.startsWith('ar');
  if (isArabic) return value.toFixed(3);
  return new Intl.NumberFormat('fr-TN', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value);
};

export const getDeliveryInstructions = (method: DeliveryMethod): string => {
  return i18n.t(`delivery.instructions.${method}`, { defaultValue: i18n.t('delivery.instructions.default') });
};

export const getDeliveryMethodLabel = (method: DeliveryMethod): string => {
  return i18n.t(`delivery.methods.${method}`, { defaultValue: method });
};

export const getPaymentMethodLabel = (method: PaymentFamily): string => {
  return i18n.t(`payment_methods.${method}`, { defaultValue: method });
};

export const getDeliveryStatusLabel = (status: DeliveryStatus): string => {
  return i18n.t(`status.${status}`, { defaultValue: status });
};

export const getOrderStatusLabel = (status: OrderStatus): string => {
  return i18n.t(`status.${status}`, { defaultValue: status });
};

/** Libellé dépôt / notification (accepte aussi les anciennes valeurs d’enum par marque). */
export const getDepositMethodLabel = (method: string): string => {
  const key = paymentMethodLocaleKey(method);
  return i18n.t(`payment_methods.${key}`, { defaultValue: method });
};

export const translateError = (error: unknown): string => {
  const message =
    error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
      ? (error as { message: string }).message
      : typeof error === 'string'
        ? error
        : 'unknown';
  const lowerMsg = message.toLowerCase();

  // 1. Connection
  if (lowerMsg.includes('failed to fetch') || lowerMsg.includes('network')) {
    return i18n.t('errors.network');
  }
  if (lowerMsg.includes('timeout')) {
    return i18n.t('errors.timeout');
  }

  // 2. Auth
  if (lowerMsg.includes('invalid login credentials')) return i18n.t('errors.auth.invalid');
  if (lowerMsg.includes('user not found')) return i18n.t('errors.auth.not_found');
  if (lowerMsg.includes('email not confirmed')) return i18n.t('errors.auth.unconfirmed');
  if (lowerMsg.includes('user already registered')) return i18n.t('errors.auth.exists');
  if (lowerMsg.includes('password should be at least')) return i18n.t('errors.auth.too_short');
  if (lowerMsg.includes('session expired')) return i18n.t('errors.auth.expired');
  if (lowerMsg.includes('rate limit')) return i18n.t('errors.auth.rate_limit');
  // Inscription : erreur générique base de données (ex. contrainte unicité email/téléphone)
  if (
    (error && typeof error === 'object' && 'code' in error && (error as { code: string }).code === 'unexpected_failure') ||
    lowerMsg.includes('database error saving new user') ||
    lowerMsg.includes('unexpected_failure')
  ) {
    return i18n.t('errors.auth.signup_failed');
  }

  // 3. DB
  if (lowerMsg.includes('duplicate key')) return i18n.t('errors.db.duplicate');
  if (lowerMsg.includes('violates foreign key')) return i18n.t('errors.db.constraint');
  if (lowerMsg.includes('permission denied')) return i18n.t('errors.db.denied');

  // 4. Storage
  if (lowerMsg.includes('object was not found')) return i18n.t('errors.storage.not_found');
  if (lowerMsg.includes('payload too large')) return i18n.t('errors.storage.too_large');

  // fallback logic
  const fallback = i18n.t(`errors.${lowerMsg}`, { defaultValue: i18n.t('common.unknown_error') });
  return fallback === i18n.t('common.unknown_error') ? message : fallback;
};

/** Longueur max recommandée pour les champs texte des préférences (nom, email, téléphone, etc.) */
export const PREFERENCE_TEXT_MAX_LENGTH = 500;

/**
 * Sanitise une saisie utilisateur pour limiter les risques XSS et injection.
 * - Supprime les caractères < et > (évite balises HTML/script)
 * - Supprime les caractères de contrôle (sauf tabulation et saut de ligne)
 * - Tronque à maxLength si fourni
 */
export const sanitizeUserInput = (value: string, maxLength: number = PREFERENCE_TEXT_MAX_LENGTH): string => {
  if (typeof value !== 'string') return '';
  let s = value
    .replace(/</g, '')
    .replace(/>/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  if (maxLength > 0 && s.length > maxLength) s = s.slice(0, maxLength);
  return s;
};

// Réexport des validations pour usage global (nom, email, téléphone tunisien, etc.)
export {
  sanitizeShortText,
  sanitizeEmail,
  sanitizeTunisianPhone,
  formatTunisianPhone,
  sanitizeLongText,
  isValidEmail,
  isValidTunisianPhone,
  commitTunisianPhone,
  MAX_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_ADDRESS_LENGTH,
  MAX_MESSAGE_LENGTH,
  MAX_SUBJECT_LENGTH,
} from './inputValidation';
